// CMaps (ISO 32000-1 §9.7.5, §9.10.3): code → CID for composite fonts and code → Unicode for ToUnicode.

import { Lexer, PCmd, PName, type PObj } from "./parse";

export class CMap {
    /** [byte length, low, high] */
    space: Array<[number, number, number]> = [];
    private cidRanges: Array<[number, number, number]> = [];
    private cidChars = new Map<number, number>();
    private bf = new Map<number, string>();
    private bfRanges: Array<[number, number, string | string[]]> = [];
    vertical = false;
    identity = false;
    /** A fixed byte width when the map is an identity (2 for Identity-H). */
    width = 2;

    static identity(vertical = false): CMap { const m = new CMap(); m.identity = true; m.vertical = vertical; m.space = [[2, 0, 0xffff]]; return m; }

    static parse(data: Uint8Array): CMap {
        const m = new CMap();
        const lx = new Lexer(data);
        const num = (s: string) => { let v = 0; for (let i = 0; i < s.length; i++) v = v * 256 + s.charCodeAt(i); return v; };
        const uni = (s: string) => { let r = ""; for (let i = 0; i + 1 < s.length; i += 2) r += String.fromCharCode((s.charCodeAt(i) << 8) | s.charCodeAt(i + 1)); if (s.length === 1) r = s; return r; };
        let t: PObj | undefined;
        let mode = "";
        let args: PObj[] = [];
        let last: PObj | undefined;
        while ((t = lx.next()) !== undefined) {
            if (t instanceof PCmd) {
                const c = t.cmd;
                if (c === "begincodespacerange" || c === "beginbfchar" || c === "beginbfrange" || c === "begincidchar" || c === "begincidrange") { mode = c.slice(5); args = []; continue; }
                if (c === "[" && mode === "bfrange") {
                    const arr: string[] = [];
                    let x: PObj | undefined;
                    while ((x = lx.next()) !== undefined && !(x instanceof PCmd && x.cmd === "]")) if (typeof x === "string") arr.push(x);
                    args.push(arr);
                    continue;
                }
                if (c.startsWith("end") && mode) {
                    if (mode === "codespacerange") for (let i = 0; i + 1 < args.length; i += 2) { const a = args[i], b = args[i + 1]; if (typeof a === "string" && typeof b === "string") m.space.push([a.length, num(a), num(b)]); }
                    else if (mode === "cidchar") for (let i = 0; i + 1 < args.length; i += 2) { const a = args[i], b = args[i + 1]; if (typeof a === "string" && typeof b === "number") m.cidChars.set(num(a), b); }
                    else if (mode === "cidrange") for (let i = 0; i + 2 < args.length; i += 3) { const a = args[i], b = args[i + 1], d = args[i + 2]; if (typeof a === "string" && typeof b === "string" && typeof d === "number") m.cidRanges.push([num(a), num(b), d]); }
                    else if (mode === "bfchar") for (let i = 0; i + 1 < args.length; i += 2) { const a = args[i], b = args[i + 1]; if (typeof a === "string" && typeof b === "string") m.bf.set(num(a), uni(b)); }
                    else if (mode === "bfrange") for (let i = 0; i + 2 < args.length; i += 3) {
                        const a = args[i], b = args[i + 1], d = args[i + 2];
                        if (typeof a !== "string" || typeof b !== "string") continue;
                        if (typeof d === "string") m.bfRanges.push([num(a), num(b), uni(d)]);
                        else if (Array.isArray(d)) m.bfRanges.push([num(a), num(b), (d as string[]).map(uni)]);
                    }
                    mode = "";
                    continue;
                }
                if (c === "def" && last instanceof PName === false && args.length === 0) { /* a plain def */ }
                last = t;
                continue;
            }
            if (mode) args.push(t);
            else if (t instanceof PName && t.name === "WMode") { const v = lx.next(); if (v === 1) m.vertical = true; }
            last = t;
        }
        return m;
    }

    static parseToUnicode(data: Uint8Array): CMap { return CMap.parse(data); }

    /** Splits a string into codes: [code, byte length]. */
    decode(s: string): Array<[number, number]> {
        const out: Array<[number, number]> = [];
        if (this.identity || !this.space.length) {
            const w = this.identity ? 2 : this.width;
            for (let i = 0; i + w <= s.length; i += w) { let v = 0; for (let k = 0; k < w; k++) v = v * 256 + s.charCodeAt(i + k); out.push([v, w]); }
            return out;
        }
        let i = 0;
        const maxLen = Math.max(...this.space.map((x) => x[0]));
        while (i < s.length) {
            let found = false;
            let v = 0;
            for (let len = 1; len <= maxLen && i + len <= s.length; len++) {
                v = v * 256 + s.charCodeAt(i + len - 1);
                if (this.space.some(([l, lo, hi]) => l === len && v >= lo && v <= hi)) { out.push([v, len]); i += len; found = true; break; }
            }
            if (!found) { const l = Math.min(this.space[0]![0], s.length - i); let c = 0; for (let k = 0; k < l; k++) c = c * 256 + s.charCodeAt(i + k); out.push([c, l]); i += l; }
        }
        return out;
    }

    cid(code: number): number {
        if (this.identity) return code;
        const c = this.cidChars.get(code);
        if (c !== undefined) return c;
        for (const [lo, hi, base] of this.cidRanges) if (code >= lo && code <= hi) return base + code - lo;
        return 0;
    }

    unicode(code: number): string | null {
        const v = this.bf.get(code);
        if (v !== undefined) return v;
        for (const [lo, hi, d] of this.bfRanges) {
            if (code < lo || code > hi) continue;
            if (Array.isArray(d)) return d[code - lo] ?? null;
            if (!d) return null;
            const last = d.charCodeAt(d.length - 1) + (code - lo);
            return d.slice(0, -1) + String.fromCharCode(last);
        }
        return null;
    }
    get hasUnicode(): boolean { return this.bf.size > 0 || this.bfRanges.length > 0; }
}
